import { useEffect } from 'react';

import { pluralize } from '@infinex-private/ts-utils/string';
import type { ChainAddressIdObject } from '@infinex/asset-config';
import {
  makeChainAddressId,
  SPAM_BALANCE_USD_THRESHOLD,
} from '@infinex/asset-config';
import { TextLink, ToastQueue } from '@infinex/ui/components';

import { useOptimisticUserAssetPreferenceUpdate } from '../../../api';
import {
  ASSET_PREFERENCES_FILTER_BY_HIDDEN_PAYLOAD,
  useGetUserAssetPreferencesQuery,
} from '../../../api/assets/assetQueries';
import type { ChainAssetBalance } from '../../balances';
import type {
  ComputedUnifiedBalances,
  UnifiedBalances,
} from '../stores/utils/computeUnifiedBalances';

/**
 * Automatically detects and hides spam balances based on infeasible USD values.
 *
 * This hook manages the user preferences query and mutation internally,
 * ensuring spam tokens are only hidden once.
 *
 * @param balances - Array of balance objects to check for spam
 * @param manageAssetVisibilityHref - URL to the manage asset visibility page, or null if not available
 */
export function useAutoHideSpamBalances(
  balances: UnifiedBalances[],
  manageAssetVisibilityHref: string | null
): void {
  const { data: hiddenPreferences, isSuccess: isHiddenPreferencesSuccess } =
    useGetUserAssetPreferencesQuery(ASSET_PREFERENCES_FILTER_BY_HIDDEN_PAYLOAD);

  const { mutate: hideAssetsMutation, isPending: isHideAssetsMutationPending } =
    useOptimisticUserAssetPreferenceUpdate({
      preferenceName: 'is_hidden',
      onSuccess: (_data, variables) => {
        ToastQueue.add('neutral', {
          title: 'Spam tokens hidden',
          content: (
            <>
              {pluralize(variables.updates.length, 'spam token')} hidden.{' '}
              {manageAssetVisibilityHref ? (
                <TextLink href={manageAssetVisibilityHref}>
                  Manage visibility
                </TextLink>
              ) : (
                'You can manage this in settings.'
              )}
            </>
          ),
          duration: 10_000,
        });
      },
    });

  useEffect(() => {
    const isAnyBalanceLoading = balances.some((b) => b.isLoading);

    if (!isHiddenPreferencesSuccess || isAnyBalanceLoading) {
      // Data not loaded
      return;
    }

    if (isHideAssetsMutationPending) {
      // Avoid duplicate mutation call
      return;
    }

    const allTokenBalances = balances
      .filter(isBalanceLoaded)
      .flatMap((b) => b.tokenBalances);

    if (allTokenBalances.length === 0) {
      return;
    }

    const alreadyHasPreferenceIds = new Set(
      hiddenPreferences.map((hiddenAssetPref) =>
        makeChainAddressId(hiddenAssetPref.chain, hiddenAssetPref.address)
      )
    );

    const newSpamTokens = detectSpamBalances(allTokenBalances).filter(
      (token) =>
        // We don't want overwrite any of the user's existing preferences
        !alreadyHasPreferenceIds.has(
          makeChainAddressId(token.chain, token.address)
        )
    );

    if (newSpamTokens.length === 0) return;

    hideAssetsMutation({
      updates: newSpamTokens.map((token) => ({
        chain: token.chain,
        address: token.address,
        preferenceName: 'is_hidden' as const,
        preferenceValue: true,
        reason: 'system_infeasible_usd_balance_hide' as const,
      })),
    });
  }, [
    isHideAssetsMutationPending,
    isHiddenPreferencesSuccess,
    hiddenPreferences,
    balances,
    hideAssetsMutation,
  ]);
}

function detectSpamBalances(
  tokenBalances: ChainAssetBalance[]
): ChainAddressIdObject[] {
  return tokenBalances
    .filter((token) => {
      const isUnverified = !token.assetId;
      const hasInfeasibleBalance =
        token.balanceUsd && token.balanceUsd > SPAM_BALANCE_USD_THRESHOLD;

      return isUnverified && hasInfeasibleBalance;
    })
    .map((token) => ({
      chain: token.chain,
      address: token.assetAddress,
    }));
}

function isBalanceLoaded(
  balance: UnifiedBalances
): balance is ComputedUnifiedBalances {
  return !balance.isLoading;
}
